import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useHasAccess } from "../hooks/useHasAccess";
import { useQuery } from "../hooks/useQuery";
import { hasLiveVersion } from "../lib/branch";
import { Button } from "./Button";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { PressButton } from "./PressButton";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

const TitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const List = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled(PressButton)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
  &:hover {
    background: var(--alpha4);
  }
`;

const Badge = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type Props = {
  spaceId: string;
  open: boolean;
  onClose: () => void;
};

export function RecentlyDeletedWorkflowsModal({
  spaceId,
  open,
  onClose,
}: Props) {
  const z = useZero();
  const deletedQuery = queries.deletedWorkflows({
    spaceId,
  });
  const [deletedWorkflows = [], deletedWorkflowsResult] = useQuery(
    open ? deletedQuery : undefined
  );
  const canWrite = useHasAccess("space:write", spaceId);
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const canManageSpace = useHasAccess("space:manage", spaceId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleRecover = () => {
    for (const id of selected) {
      z.mutate(sharedMutators.recoverWorkflow({ workflowId: id }));
    }
    setSelected(new Set());
    onClose();
  };

  const handlePermanentDelete = () => {
    for (const id of selected) {
      z.mutate(sharedMutators.permanentlyDeleteWorkflow({ workflowId: id }));
    }
    setSelected(new Set());
    onClose();
  };

  const noneSelected = selected.size === 0;
  // Recovering a workflow with a live version needs space:push_live.
  const liveBlocked =
    !canPushLive &&
    deletedWorkflows.some(
      (w) => selected.has(w.id) && hasLiveVersion(w.branches)
    );

  return (
    <Modal open={open} onClose={onClose}>
      <Content>
        <TitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Recently deleted
          </Text>
          {deletedWorkflows.length > 0 ? (
            <Text variant="text-regular" color="var(--text-secondary)">
              Deleted workflows are kept here for 30 days, then permanently
              removed.
            </Text>
          ) : deletedWorkflowsResult.type === "complete" ? (
            <Text variant="text-regular" color="var(--text-secondary)">
              Nothing here — anything you delete will be recoverable for 30
              days.
            </Text>
          ) : null}
        </TitleBlock>
        {deletedWorkflows.length > 0 ? (
          <Body>
            <List>
              {deletedWorkflows.map((w) => {
                const badgeConfig = parseBadgeConfig(w.badgeSeed, w.id);
                const isSelected = selected.has(w.id);
                return (
                  <Row
                    key={w.id}
                    data-no-pill=""
                    onPress={() => toggle(w.id)}
                    aria-pressed={isSelected}
                  >
                    <Badge>
                      <CircleMesh
                        key={w.badgeSeed}
                        targetCircleSize={8}
                        seed={badgeConfig.seed}
                        mutingIntensity={0}
                        animate={false}
                        restrictedColors={badgeConfig.colors}
                        grayscale={w.pausedAt != null}
                      />
                    </Badge>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        variant="text-regular"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {w.name}
                      </Text>
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        opacity: isSelected ? 1 : 0.12,
                      }}
                    >
                      <Icon
                        name={isSelected ? "check-filled" : "check-empty"}
                        size={16}
                      />
                    </span>
                  </Row>
                );
              })}
            </List>
            <Footer>
              <Tooltip
                content="You don’t have permission to restore a live version"
                hidden={!liveBlocked}
              >
                <Button
                  size="l"
                  variant="secondary"
                  onClick={handleRecover}
                  disabled={noneSelected || !canWrite || liveBlocked}
                >
                  Recover
                </Button>
              </Tooltip>
              <Button
                size="l"
                variant="danger"
                onClick={handlePermanentDelete}
                disabled={noneSelected || !canManageSpace}
              >
                Delete permanently
              </Button>
            </Footer>
          </Body>
        ) : null}
      </Content>
    </Modal>
  );
}
